/** @format */

/**
 * 会话链路 RootSpans 的时间轴视图（ECharts custom 甘特条）：
 * 顶部为按「会话起止时间」归一化的秒刻度；每行条形起点由 StartTime 决定，长度为耗时占比。
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { _$, dayjs } from '@king-design/react'
import { EchartReact } from 'src/components'
import type { SessionDetailRootSpan } from './typescript'
import {
  buildTimelineBarData,
  buildTimelineChartOption,
  getTimelineChartHeight,
  getTimelineRange
} from './timelineChart'
import type { TimelineBarDatum } from './timelineChart'

interface RootSpansTimelineProps {
  className?: string
  spans: SessionDetailRootSpan[]
  toTraceDetail: (trace: { TraceId: string; StartTime: string }) => void
  /** 点击时间轴条目，锚点滚动到对应 SpanItem */
  onTraceClick?: (traceId: string) => void
}

const RootSpansTimeline: React.FC<RootSpansTimelineProps> = ({
  className,
  spans,
  toTraceDetail,
  onTraceClick
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)

  const sorted = useMemo(
    () => [...spans].sort((a, b) => dayjs(a.StartTime).valueOf() - dayjs(b.StartTime).valueOf()),
    [spans]
  )

  const timelineRange = useMemo(() => getTimelineRange(sorted), [sorted])
  const barData = useMemo(
    () => buildTimelineBarData(sorted, timelineRange),
    [sorted, timelineRange]
  )
  const chartHeight = useMemo(() => getTimelineChartHeight(sorted.length), [sorted.length])

  const chartOption = useMemo(
    () => buildTimelineChartOption(sorted, timelineRange, barData),
    [sorted, timelineRange, barData]
  )

  useEffect(() => {
    const el = wrapRef.current
    if (!el) {
      return
    }
    const handleTooltipLinkClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-timeline-trace]')
      if (!target || !el.contains(target)) {
        return
      }
      event.preventDefault()
      toTraceDetail({
        TraceId: target.dataset.traceId || '',
        StartTime: target.dataset.startTime || ''
      })
    }
    el.addEventListener('click', handleTooltipLinkClick)
    return () => el.removeEventListener('click', handleTooltipLinkClick)
  }, [toTraceDetail])

  const handleChartClick = useCallback(
    (params: {
      componentType?: string
      seriesType?: string
      data?: TimelineBarDatum
      dataIndex?: number
    }) => {
      if (params.componentType !== 'series' || params.seriesType !== 'custom') {
        return
      }
      const traceId = params.data?.span?.TraceId ?? barData[params.dataIndex ?? -1]?.span?.TraceId
      if (traceId) {
        onTraceClick?.(traceId)
      }
    },
    [barData, onTraceClick]
  )

  if (!sorted.length) {
    return <div className={`${className} root-span-timeline-empty`}>{_$('暂无链路数据')}</div>
  }

  return (
    <div className={className} ref={wrapRef}>
      <EchartReact
        className="root-span-timeline-chart"
        style={{ height: chartHeight }}
        option={chartOption}
        notMerge
        lazyUpdate
        onEvents={{ click: handleChartClick }}
      />
    </div>
  )
}

export default styled(RootSpansTimeline)`
  .root-span-timeline-chart {
    width: 100%;
  }

  &.root-span-timeline-empty {
    padding: 48px;
    text-align: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }
`
